import React, { useContext, useMemo } from 'react';

import ReadOnlyDataCard, { IReadOnlyField } from './ReadOnlyDataCard/ReadOnlyDataCard';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import useCollaboratorMembershipData from '../../../../shared/hooks/useCollaboratorMembershipData/useCollaboratorMembershipData';
import { ICollaboratorPlayerDataState } from '../../../../shared/hooks/useCollaboratorPlayerData/useCollaboratorPlayerData';
import { TRANSLATION_COLLABORATORS } from '../../../../shared/translations/collaborators';
import { formatDateForDisplay } from '../../../../shared/utils/datetime/datetime';
import { toLocaleNumberFormat } from '../../../../shared/utils/utils';

import './ContractDetails.scss';

interface IContractDetails {
  email: string;
  currencySalary?: string;
  playerDataState: ICollaboratorPlayerDataState;
  onRetryPlayerData: () => void;
}

const ContractDetails = ({ email, currencySalary, playerDataState, onRetryPlayerData }: IContractDetails) => {
  const { contractCard, addressCard } = TRANSLATION_COLLABORATORS.detail.contractDetails;
  const { companyData, isUSA } = useContext(CompanyContext);

  const { membershipDataState, refetchMembershipData } = useCollaboratorMembershipData(companyData.id, email, true);

  const contractFields = useMemo<IReadOnlyField[]>(() => {
    const { annualSalary, hoursWorkedPerWeek, dateOfHire, employeeIdNumber } = membershipDataState.data ?? {};

    return [
      {
        label: contractCard.annualSalary,
        value:
          annualSalary !== null && annualSalary !== undefined
            ? toLocaleNumberFormat(annualSalary, {
                style: 'currency',
                currency: currencySalary,
                useGrouping: true,
                maximumFractionDigits: 2
              })
            : null
      },
      {
        label: contractCard.weeklyHours,
        value: hoursWorkedPerWeek !== null && hoursWorkedPerWeek !== undefined ? `${hoursWorkedPerWeek}` : null
      },
      {
        label: contractCard.dateOfHire,
        value: dateOfHire ? formatDateForDisplay(dateOfHire, isUSA ?? false) : null
      },
      {
        label: contractCard.employeeIdNumber,
        value: employeeIdNumber ?? null
      }
    ];
  }, [membershipDataState.data, currencySalary, isUSA, contractCard]);

  const addressFields = useMemo<IReadOnlyField[]>(() => {
    const { address1, address2, city, state, zipCode } = playerDataState.data ?? {};

    return [
      { label: addressCard.address1, value: address1 ?? null },
      { label: addressCard.address2, value: address2 ?? null },
      { label: addressCard.city, value: city ?? null },
      { label: addressCard.state, value: state ?? null },
      { label: addressCard.zipCode, value: zipCode ?? null }
    ];
  }, [playerDataState.data, addressCard]);

  return (
    <div className="collaborator-contract-details-section flex flex-col w-full">
      <ReadOnlyDataCard
        title={contractCard.title}
        fields={contractFields}
        isLoading={membershipDataState.isLoading}
        isError={membershipDataState.error}
        onRetry={refetchMembershipData}
      />
      <ReadOnlyDataCard
        title={addressCard.title}
        fields={addressFields}
        isLoading={playerDataState.isLoading}
        isError={playerDataState.error}
        onRetry={onRetryPlayerData}
      />
    </div>
  );
};

export default ContractDetails;
